/**
 * Call-routing health helpers (roadmap #54).
 *
 * A QR (kiosk) can only receive calls if its "default staff" is a real agent
 * (role 3, present in the staff list) that has a registered device. The audit
 * found ~45% of kiosks misconfigured (a client/owner account or a deviceless
 * staff set as default). These helpers are shared by the QR form (to constrain
 * and warn) and the routing-health view (to list broken kiosks).
 *
 * A device is considered registered when `device_os` is set — the same signal
 * the backend uses to decide whether a staff member is reachable.
 */

export type RoutingStatus = 'ok' | 'no_default' | 'default_not_agent' | 'default_no_device';

export const hasDevice = (staff?: { device_os?: unknown } | null): boolean =>
  !!(staff && staff.device_os);

/**
 * Evaluate a QR's default-staff routing given a lookup of role-3 staff by id.
 * `staffById` must only contain staff (role 3); an id missing from it therefore
 * means the configured default is not a valid agent (a client/owner/inactive).
 */
export const evaluateQrRouting = (
  defaultStaffId: number | string | null | undefined,
  staffById: Map<number, { device_os?: unknown }>
): RoutingStatus => {
  if (defaultStaffId == null || defaultStaffId === '' || Number(defaultStaffId) === 0) {
    return 'no_default';
  }
  const staff = staffById.get(Number(defaultStaffId));
  if (!staff) return 'default_not_agent';
  if (!hasDevice(staff)) return 'default_no_device';
  return 'ok';
};
